import AddIcon from '@mui/icons-material/Add';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, Grid, IconButton, Slider, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import BpmnViewer, { BpmnViewerRef } from '../BpmnViewer';
import DiagramModal from '../../BpmnIoDiagram/DiagramModel';
import { saveDiagram } from '../../../services/diagramService';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import { httpService } from '../../../utility/httpService';
import { httpConstants } from '../../../constants';
import { API_BASE } from '../../../config/env';

interface ImportEditPageProps {
  onClose: (savedUrl?: string) => void;
  xml: string;
  data: any;
  setData: (data: any) => void;
  diagramId?: string;
  subprocessId?: string;
  onNameUpdated?: (subprocessId?: string, newName?: string) => void;
}

interface ElementData {
  id: string;
  type: string;
  name: string;
  description?: string;
  aiEnablers?: string[];
}

interface ElementModal {
  isOpen: boolean;
  elementData: ElementData | null;
  activeTab: 'aiHubble' | 'opportunities';
}

const ImportEditPage: React.FC<ImportEditPageProps> = ({
  xml,
  data,
  setData,
  onClose,
  diagramId,
  subprocessId,
  onNameUpdated,
}) => {
  const { diagramData, processedData } = data;

  const bpmnViewerRef = useRef<BpmnViewerRef>(null);

  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [panX, setPanX] = useState<number>(0);
  const [modal, setModal] = useState<ElementModal>({
    isOpen: false,
    elementData: null,
    activeTab: 'aiHubble',
  });
  const [currentDisplayXml, setCurrentDisplayXml] = useState<string>('');
  const [isEditingHeader, setIsEditingHeader] = useState<boolean>(false);
  const [editedSubprocessName, setEditedSubprocessName] = useState<string>(
    diagramData?.subprocessName || ''
  );
  const [isUpdatingName, setIsUpdatingName] = useState<boolean>(false);

  useEffect(() => {
    setCurrentDisplayXml(`${xml}`);
  }, []);

  // Zoom Sync
  const syncZoomLevel = () => {
    if (bpmnViewerRef.current?.getCurrentZoom) {
      const actualZoom = bpmnViewerRef.current.getCurrentZoom();
      setZoomLevel(Math.round(actualZoom * 100));
    }
  };

  const handleZoomIn = () => {
    bpmnViewerRef.current?.zoomIn();
    syncZoomLevel();
  };

  const handleZoomOut = () => {
    bpmnViewerRef.current?.zoomOut();
    syncZoomLevel();
  };

  const handleZoomFit = () => {
    bpmnViewerRef.current?.zoomFit();
    syncZoomLevel();
  };

  const handleZoomSliderChange = (_: Event, newValue: number | number[]) => {
    const newZoom = newValue as number;
    setZoomLevel(newZoom);
    bpmnViewerRef.current?.setZoom(newZoom / 100);
  };

  const handlePanSliderChange = (_: Event, newValue: number | number[]) => {
    const newPanX = newValue as number;
    setPanX(newPanX);
    bpmnViewerRef.current?.setPanX(newPanX);
  };

  const handlePanLeft = () => {
    const newPanX = panX - 100;
    setPanX(newPanX);
    bpmnViewerRef.current?.setPanX(newPanX);
  };

  const handlePanRight = () => {
    const newPanX = panX + 100;
    setPanX(newPanX);
    bpmnViewerRef.current?.setPanX(newPanX);
  };

  const handleElementClick = useCallback(
    (event: any) => {
      const element = event.element;
      const allowedTypes = [
        'bpmn:Task',
        'bpmn:UserTask',
        'bpmn:ServiceTask',
        'bpmn:SendTask',
        'bpmn:ReceiveTask',
        'bpmn:ManualTask',
        'bpmn:BusinessRuleTask',
        'bpmn:ScriptTask',
        'bpmn:Activity',
        'bpmn:SubProcess',
      ];

      if (allowedTypes.includes(element.type) && processedData) {
        const elementDetails = processedData[element.id];

        if (elementDetails) {
          setModal({
            isOpen: true,
            activeTab: 'aiHubble',
            elementData: {
              id: element.id,
              type: element.type,
              name: elementDetails.name,
              description: elementDetails.description,
            },
          });
        } else {
          setModal({
            isOpen: true,
            activeTab: 'aiHubble',
            elementData: {
              id: element.id,
              type: element.type,
              name: element.businessObject.name || 'Unnamed Element',
              description: 'No detailed description available.',
              aiEnablers: [],
            },
          });
        }
      }
    },
    [processedData]
  );

  useEffect(() => {
    if (!currentDisplayXml) return;

    const timer = setTimeout(() => {
      const eventBus = bpmnViewerRef.current?.getEventBus?.();
      if (!eventBus) return;

      eventBus.on('element.click', handleElementClick);
    }, 200); // small delay to let importXML finish

    return () => {
      clearTimeout(timer);
      const eventBus = bpmnViewerRef.current?.getEventBus?.();
      if (eventBus) {
        eventBus.off('element.click', handleElementClick);
      }
    };
  }, [currentDisplayXml, handleElementClick]);

  const handleSave = async () => {
    if (bpmnViewerRef.current?.saveXML) {
      const updatedXml = await bpmnViewerRef.current.saveXML();
      if (updatedXml) {
        setData(updatedXml);
        onClose();
      }
    }
  };

  const handleEditHeaderClick = () => {
    setIsEditingHeader(true);
  };

  const handleHeaderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedSubprocessName(e.target.value);
  };

  const handleSaveHeaderName = async () => {
    if (!editedSubprocessName.trim()) {
      ShowToast({
        message: 'Process name cannot be empty',
        type: ToastTypeEnum.Error,
      });
      return;
    }

    if (editedSubprocessName === diagramData?.subprocessName) {
      setIsEditingHeader(false);
      return;
    }

    setIsUpdatingName(true);
    try {
      // Update the diagramData with the new name
      const updatedDiagramData = {
        ...diagramData,
        subprocessName: editedSubprocessName,
      };

      // First attempt to update Level2 (subprocess) name if we have subprocessId
      if (subprocessId) {
        try {
          const url = `${API_BASE}/api/v1/e2e_taxonomy/level2/${encodeURIComponent(subprocessId)}/`;
          await httpService(httpConstants.METHOD_TYPE.PATCH, url, { name: editedSubprocessName });
        } catch (err) {
          console.warn('Warning: Level2 PATCH failed for subprocess name update', err);
        }
      }

      // Also attempt to save diagram metadata (fallback) similar to other flows
      if (diagramId) {
        try {
          const formData = new FormData();

          // If we have existing diagram data blob, merge and update subprocessName
          const existingData = data?.processedData || diagramData?.xml_data || {};
          const mergedData = { ...existingData, subprocessName: editedSubprocessName };

          formData.append('data', JSON.stringify(mergedData));

          // If XML string is available, include it to be safe
          if (diagramData?.xml_string) {
            formData.append(
              'xmlData',
              new File([diagramData.xml_string], 'diagram.xml', { type: 'text/xml' })
            );
          }

          await saveDiagram(diagramId, formData);
        } catch (err) {
          console.warn('Warning: saveDiagram failed for subprocess name update', err);
          // still proceed to update UI locally
        }
      }

      // Notify parent with updated diagram metadata so UI reflects new name
      setData({ diagramData: updatedDiagramData });
      if (onNameUpdated) onNameUpdated(subprocessId, editedSubprocessName);

      ShowToast({
        message: 'Process name updated successfully',
        type: ToastTypeEnum.Success,
      });

      setIsEditingHeader(false);
    } catch (error) {
      console.error('Failed to update process name:', error);
      ShowToast({
        message: 'Failed to update process name',
        type: ToastTypeEnum.Error,
      });
      // Revert to original name on error
      setEditedSubprocessName(diagramData?.subprocessName || '');
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleCancelHeaderEdit = () => {
    setEditedSubprocessName(diagramData?.subprocessName || '');
    setIsEditingHeader(false);
  };

  const handleHeaderKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveHeaderName();
    } else if (e.key === 'Escape') {
      handleCancelHeaderEdit();
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: '#F4F6FF',
        zIndex: 1299,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          minHeight: '60px',
          backgroundColor: '#F4F6FF',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1,
            px: 3,
            py: 2,
          }}
        >
          <Grid sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEditingHeader ? (
              <TextField
                value={editedSubprocessName}
                onChange={handleHeaderNameChange}
                onKeyDown={handleHeaderKeyPress}
                onBlur={handleSaveHeaderName}
                autoFocus
                size="small"
                disabled={isUpdatingName}
                sx={{
                  width: '300px',
                  '& .MuiOutlinedInput-root': {
                    fontSize: '16px',
                    fontWeight: 700,
                  },
                }}
              />
            ) : (
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px' }}>
                {diagramData?.subprocessName}
              </Typography>
            )}
            <IconButton
              onClick={isEditingHeader ? handleCancelHeaderEdit : handleEditHeaderClick}
              size="small"
              sx={{ color: '#666' }}
              disabled={isUpdatingName}
              title={isEditingHeader ? 'Cancel' : 'Edit'}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Grid>

          <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '14px' }}>
            {` ${diagramData?.functionName} / ${diagramData?.e2eName} / ${diagramData?.processName}`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#1A55F9',
              '&:hover': { backgroundColor: '#1A55F9' },
              '&:disabled': { backgroundColor: '#ccc' },
              textTransform: 'none',
              width: '80px',
              height: '40px',
              borderRadius: '5px',
              fontSize: '14px',
            }}
            onClick={handleSave}
          >
            Save
          </Button>

          <IconButton onClick={() => onClose()} sx={{ color: '#666' }}>
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: '#F4F6FF',
            position: 'relative',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {currentDisplayXml ? (
            <BpmnViewer ref={bpmnViewerRef} xml={currentDisplayXml} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: '#666',
              }}
            >
              <Typography>Loading static diagram...</Typography>
            </Box>
          )}
        </Box>

        {/* Zoom + Pan Controls */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            display: 'flex',
            gap: '8px',
          }}
        >
          {/* Zoom Controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              padding: '0px 4px',
              gap: '4px',
              width: '160px',
              height: '30px',
            }}
          >
            <IconButton
              onClick={handleZoomOut}
              sx={{ width: '24px', height: '24px', color: '#666' }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Slider
              value={zoomLevel}
              onChange={handleZoomSliderChange}
              min={10}
              max={200}
              sx={{ width: '60px' }}
            />
            <IconButton
              onClick={handleZoomIn}
              sx={{ width: '24px', height: '24px', color: '#666' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Pan Controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              padding: '0px 4px',
              gap: '4px',
              width: '160px',
              height: '30px',
            }}
          >
            <IconButton
              onClick={handleZoomFit}
              sx={{ width: '24px', height: '24px', color: '#666' }}
            >
              <ArrowOutwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handlePanLeft}
              sx={{ width: '24px', height: '24px', color: '#666' }}
            >
              <ArrowBackOutlinedIcon fontSize="small" />
            </IconButton>
            <Slider
              value={panX}
              onChange={handlePanSliderChange}
              min={-1000}
              max={1000}
              step={10}
              sx={{ width: '60px' }}
            />
            <IconButton
              onClick={handlePanRight}
              sx={{ width: '24px', height: '24px', color: '#666' }}
            >
              <ArrowForwardOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
      {modal.isOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            left: '65%',
            height: '100%',
            width: '80%',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }}
        >
          <DiagramModal
            isOpen={modal.isOpen}
            elementData={modal.elementData}
            activeTab={modal.activeTab}
            opportunities={[]}
            onClose={() => setModal((prev) => ({ ...prev, isOpen: false }))}
            onTabChange={(tab) => setModal((prev) => ({ ...prev, activeTab: tab }))}
          />
        </Box>
      )}
    </Box>
  );
};

export default ImportEditPage;
